import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import Button from '../../../base/ui/components/web/Button';
import { BUTTON_TYPES } from '../../../base/ui/constants.any';
import { startInterpreterOfferAlert } from '../../interpreterOfferAlert.web';
import { offerTiming } from '../../offerTiming';
const useStyles = makeStyles()(() => ({
    popup: {
        position: 'fixed',
        top: '20px',
        right: '20px',
        backgroundColor: '#1a2332',
        border: '2px solid #4a9eff',
        borderRadius: '8px',
        boxSizing: 'border-box',
        padding: '20px',
        width: 'min(400px, calc(100vw - 40px))',
        maxHeight: 'calc(100vh - 40px)',
        overflowY: 'auto',
        overflowWrap: 'anywhere',
        zIndex: 1000,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
        animation: 'slideInRight 0.3s ease-out',
        '&:focus-visible': {
            outline: '3px solid #ffffff',
            outlineOffset: '3px'
        },
        '@media (prefers-reduced-motion: reduce)': {
            animation: 'none'
        }
    },
    inboundPopup: {
        backgroundColor: '#2a2114',
        borderColor: '#f59e0b',
        boxShadow: '0 8px 28px rgba(245, 158, 11, 0.22)'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '16px'
    },
    title: {
        color: '#ffffff',
        fontSize: '18px',
        fontWeight: 'bold',
        margin: 0
    },
    closeButton: {
        alignItems: 'center',
        background: 'none',
        border: 'none',
        borderRadius: '4px',
        color: '#ffffff',
        cursor: 'pointer',
        display: 'flex',
        fontSize: '20px',
        justifyContent: 'center',
        minHeight: '44px',
        minWidth: '44px',
        padding: '4px',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)'
        },
        '&:focus-visible': {
            outline: '3px solid #ffffff',
            outlineOffset: '2px'
        }
    },
    content: {
        color: '#e0e0e0',
        marginBottom: '20px',
        lineHeight: '1.4'
    },
    clientName: {
        color: '#75baff',
        fontWeight: 'bold'
    },
    inboundRoute: {
        color: '#fde7b0',
        fontSize: '17px',
        fontWeight: 700,
        margin: '4px 0 8px'
    },
    language: {
        color: '#ffd54f',
        fontWeight: 'bold'
    },
    timing: {
        fontSize: '13px',
        fontWeight: 700,
        margin: '10px 0 0'
    },
    expired: {
        color: '#ffb4ab'
    },
    metadata: {
        backgroundColor: 'rgba(255, 255, 255, 0.06)',
        borderRadius: '6px',
        fontSize: '12px',
        marginTop: '10px',
        padding: '10px'
    },
    metadataRow: {
        margin: '2px 0'
    },
    demoNotice: {
        backgroundColor: 'rgba(255, 199, 111, 0.16)',
        border: '1px solid #ffc76f',
        borderRadius: '6px',
        color: '#ffffff',
        fontWeight: 600,
        margin: '0 0 12px',
        padding: '10px'
    },
    error: {
        backgroundColor: 'rgba(220, 53, 69, 0.18)',
        border: '1px solid #ff8a93',
        borderRadius: '6px',
        color: '#ffffff',
        margin: '0 0 14px',
        padding: '10px'
    },
    actions: {
        display: 'flex',
        gap: '12px'
    },
    acceptButton: {
        flex: 1,
        minHeight: '44px',
        backgroundColor: '#28a745',
        border: 'none',
        '&:hover': {
            backgroundColor: '#218838'
        }
    },
    declineButton: {
        flex: 1,
        minHeight: '44px',
        backgroundColor: '#dc3545',
        border: 'none',
        '&:hover': {
            backgroundColor: '#c82333'
        }
    }
}));
const InterpreterRequestPopup = ({ actionsBlocked = false, actionError, actionPending = null, request, onAccept, onDecline, onDismiss }) => {
    const { classes } = useStyles();
    const popupRef = useRef(null);
    const [now, setNow] = useState(Date.now());
    const isInboundHearing = request.requestKind === 'inbound_hearing';
    const callerLast4 = request.targetPhoneLast4
        || String(request.targetPhone || '').replace(/\D/g, '').slice(-4)
        || 'unknown';
    const timing = offerTiming(request, now);
    const identifier = request.id.replace(/[^A-Za-z0-9_-]/g, '-');
    const titleId = `interpreter-request-${identifier}-title`;
    const contentId = `interpreter-request-${identifier}-content`;
    const timingId = `interpreter-request-${identifier}-timing`;
    const actionsDisabled = timing.expired || actionsBlocked || Boolean(actionPending);
    useEffect(() => startInterpreterOfferAlert(request), [request]);
    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);
    useEffect(() => {
        const previousFocus = document.activeElement instanceof HTMLElement
            ? document.activeElement : null;
        popupRef.current?.focus();
        return () => {
            if (previousFocus && document.contains(previousFocus)) {
                previousFocus.focus();
            }
        };
    }, [request.id]);
    const handleKeyDown = (event) => {
        if (event.key === 'Escape' && !actionPending) {
            event.preventDefault();
            event.stopPropagation();
            onDismiss(request.id);
        }
    };
    return (React.createElement("div", { "aria-busy": Boolean(actionPending), "aria-describedby": `${contentId} ${timingId}`, "aria-labelledby": titleId, className: `${classes.popup} ${isInboundHearing ? classes.inboundPopup : ''}`, onKeyDown: handleKeyDown, ref: popupRef, role: 'dialog', tabIndex: -1 },
        React.createElement("div", { className: classes.header },
            React.createElement("h2", { className: classes.title, id: titleId }, isInboundHearing ? 'Inbound hearing call' : 'Interpreter request'),
            React.createElement("button", { "aria-label": 'Minimize interpreter request', className: classes.closeButton, disabled: Boolean(actionPending), onClick: () => onDismiss(request.id), title: 'Minimize interpreter request' },
                React.createElement("span", { "aria-hidden": 'true' }, "\u00D7"))),
        React.createElement("div", { className: classes.content, id: contentId },
            request.demoMode && (React.createElement("p", { className: classes.demoNotice, role: 'note' }, "Demo call \u2014 not payable. This client is a Demo account, so the call is mock-billed and creates no interpreter payable.")),
            isInboundHearing ? (React.createElement(React.Fragment, null,
                React.createElement("p", { className: classes.inboundRoute },
                    "Hearing caller ending ",
                    callerLast4,
                    " \u2192 ",
                    request.clientName),
                React.createElement("p", null,
                    "Answer the held hearing caller first. Ring ",
                    request.clientName,
                    " when you are ready to interpret."))) : (React.createElement("p", null,
                "Client ",
                React.createElement("span", { className: classes.clientName }, request.clientName),
                " is requesting an interpreter for ",
                React.createElement("span", { className: classes.language }, request.language),
                ".")),
            React.createElement("p", null,
                "Language: ",
                React.createElement("span", { className: classes.language }, request.language)),
            React.createElement("p", { className: `${classes.timing} ${timing.expired ? classes.expired : ''}`, id: timingId, role: timing.expired ? 'status' : undefined }, timing.label),
            (request.scheduledStartAt || request.industryTag || request.department || request.costCenter
                || request.instructions || request.vcoEnabled) && (React.createElement("div", { className: classes.metadata },
                request.vcoEnabled && React.createElement("p", { className: classes.metadataRow }, "VCO requested"),
                request.scheduledStartAt && (React.createElement("p", { className: classes.metadataRow },
                    "Scheduled: ",
                    new Date(request.scheduledStartAt).toLocaleString())),
                request.industryTag && React.createElement("p", { className: classes.metadataRow },
                    "Industry: ",
                    request.industryTag),
                request.department && React.createElement("p", { className: classes.metadataRow },
                    "Department: ",
                    request.department),
                request.costCenter && React.createElement("p", { className: classes.metadataRow },
                    "Cost center: ",
                    request.costCenter),
                request.instructions && (React.createElement("p", { className: classes.metadataRow },
                    isInboundHearing ? 'Notes' : 'Instructions',
                    ": ",
                    request.instructions))))),
        actionError && React.createElement("div", { className: classes.error, role: 'alert' }, actionError),
        React.createElement("div", { className: classes.actions },
            React.createElement(Button, { accessibilityLabel: isInboundHearing ? 'Accept and answer inbound hearing call' : 'Accept interpreter request', className: classes.acceptButton, disabled: actionsDisabled, onClick: () => onAccept(request.id), label: actionPending === 'accept'
                    ? 'Accepting…'
                    : timing.expired ? 'Expired' : isInboundHearing ? 'Accept & Answer' : 'Accept', type: BUTTON_TYPES.PRIMARY }),
            React.createElement(Button, { accessibilityLabel: 'Decline interpreter request', className: classes.declineButton, disabled: actionsDisabled, onClick: () => onDecline(request.id), label: actionPending === 'decline' ? 'Declining…' : 'Decline', type: BUTTON_TYPES.SECONDARY }))));
};
export default InterpreterRequestPopup;
